import { decodeModel, fromBase64, type Model } from './codec';
import { FEATURE_DIM, featureKey } from './features';
import { classKey } from './taxonomy';

type BlobModule = { ROUTER_MODEL_BASE64: string };

/**
 * The model lives in its own chunk (a few hundred kilobytes of base64), so the CRM does not carry it until the
 * Agents appear. `load` starts the import once and is safe to call again and again; if the chunk cannot be fetched
 * or is not the model the code expects, it logs why, answers undefined (the router keeps answering by keyword) and
 * the next call tries again instead of remembering the failure for the whole session. (Some browsers keep a failed
 * dynamic import in their module map until the page reloads, so the retry can fail the same way; the keywords carry on.)
 */
export function createModelLoader(importBlob: () => Promise<BlobModule>): { load: () => Promise<Model | undefined>; get: () => Model | undefined } {
  let ready: Model | undefined;
  let loading: Promise<Model | undefined> | undefined;
  return {
    load() {
      loading ??= importBlob()
        .then(({ ROUTER_MODEL_BASE64 }) => {
          const model = decodeModel(fromBase64(ROUTER_MODEL_BASE64), classKey(), featureKey(FEATURE_DIM));
          if (model.dim !== FEATURE_DIM) throw new Error('The router model has another size than the code reads: train it again.');
          ready = model;
          return model;
        })
        .catch((error: unknown) => {
          console.error('The router model could not be loaded; the team answers by keyword only.', error);
          loading = undefined;
          return undefined;
        });
      return loading;
    },
    get: () => ready,
  };
}

const shipped = createModelLoader(() => import('./model.blob'));

export function loadRouterModel(): Promise<Model | undefined> {
  return shipped.load();
}

/** The model once it has loaded; undefined before that or if it never did. */
export function routerModel(): Model | undefined {
  return shipped.get();
}
